import { getSelectedCountryCode } from "@utils/countryUtils";
import { check401, getValidAccessToken } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";
import { PENALTY_EXPOSURE_PENDING_DISPLAY } from "@constants/dashboardPenaltyExposure";
import { isCmsNursingHomePdcFacility, isCmsNursingHomePdcSlug, type CmsNursingHomePdcMeta, type CmsNursingHomePdcFacilityLike } from "@utils/cmsNursingHomePdc";

const API_BASE_URL = getApiOrigin();
const DASHBOARD_PATH = "/api/v1/facility-management/facility";

/** Default 60s — cold dashboard BFF (historic penalties + SOM aggregator) can exceed 20s. */
export function resolveDashboardFetchTimeoutMs(): number {
  const raw = process.env.REACT_APP_DASHBOARD_FETCH_TIMEOUT_MS;
  if (raw != null && raw !== "") {
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return 60000;
}

export const DASHBOARD_FETCH_TIMEOUT_MS = resolveDashboardFetchTimeoutMs();

export interface DashboardSparklinePoint {
  label: string;
  value: number;
}

export { PENALTY_EXPOSURE_PENDING_DISPLAY };

export interface DashboardPenaltyExposureTile {
  total_exposure: number;
  total_exposure_display: string;
  /** False until compliance penalty data has been aggregated (avoids misleading $0). */
  exposure_calculated?: boolean;
  quarter_delta: number;
  quarter_delta_display: string;
  quarter_delta_direction: "up" | "down" | "flat";
  sparkline_points: DashboardSparklinePoint[];
  /** When false, omit quarter-over-quarter trend and sparkline (no prior-quarter base). */
  has_quarter_trend?: boolean;
  /** SSOT-38: RHC/FQHC use denial/termination, not dollar CMP exposure. */
  enforcement_regime?: "cmp" | "denial_of_payment_termination" | null;
  exposure_note?: string | null;
}

export interface DashboardSomRegulationTag {
  tag: string;
  section_id?: number | null;
}

export interface DashboardRegulationUpdatesTile {
  count_this_month: number;
  regulation_tags: string[];
  som_tags: DashboardSomRegulationTag[];
  last_som_updated_at?: string | null;
  last_som_updated_display: string;
  facility_type_label?: string | null;
  som_appendix_letter?: string | null;
  /** Optional preformatted subtitle from Rules API (facility type + appendix). */
  subtitle?: string | null;
  lookback_days: number;
}

/** Navigation state for Regulatory Updates (SOM) on /dashboard/discover */
export interface DiscoverSomNavigationState {
  category: "latest-updates";
  somTag?: string;
  sectionId?: number;
}

export interface DashboardAnalysesRunTile {
  count_90d: number;
  last_run_at: string | null;
  analysis_type_summary: string;
}

export interface DashboardCalendarEvent {
  date: string;
  name: string;
  event_type: "required" | "recommended";
  status: "upcoming" | "overdue";
}

export interface DashboardCalendarStrip {
  month_label: string;
  year: number;
  events: DashboardCalendarEvent[];
}

export interface DashboardHistoricPenaltyYear {
  year: number;
  amount: number;
  violation_count: number;
  amount_display: string;
}

export interface DashboardHistoricPenaltiesSummary {
  total_amount: number;
  total_violations: number;
  most_cited_tag: string | null;
  most_cited_count: number;
  yearly_totals: DashboardHistoricPenaltyYear[];
  has_data: boolean;
}

export interface DashboardHistoricPenaltiesIndustrySummary extends DashboardHistoricPenaltiesSummary {
  facility_type_label: string | null;
  facilities_analyzed_count: number;
  industry_data_source?: "cms_open_data" | "enforcement_snapshots" | null;
}

export interface DashboardHistoricPenaltiesFacilitySummary extends DashboardHistoricPenaltiesSummary {}

export interface DashboardActionRequiredItem {
  id: string;
  urgency: "overdue" | "due_today" | "critical_finding";
  title: string;
  description: string;
  feature_link: string;
  feature_label: string;
  relative_timestamp: string | null;
}

export interface DashboardRegulationImpactItem {
  regulation_tag: string;
  title: string;
  description: string;
  impact_level: "affects_policy" | "review_suggested" | "no_action";
  impact_label: string;
  discover_path?: string | null;
  source_document_name?: string | null;
  analysis_history_id?: number | null;
  reference_codes?: string[];
  regulation_citation?: string | null;
  discover_reference_id?: string | null;
}

export interface DashboardDocumentExpiryItem {
  document_name: string;
  expiry_date: string;
  days_remaining: number;
  badge_label: string;
  badge_tone: "red" | "amber" | "teal" | "expired";
}

export interface DashboardProfileCompleteness {
  completed_count: number;
  total_count: number;
  completeness_percentage: number;
  missing_fields: string[];
  /** SSOT-59 — optional gaps that still pull the weighted score down */
  missing_optional_fields?: string[];
}

/** Legacy dashboard field before industry/facility split. */
type LegacyHistoricPenalties = Partial<DashboardHistoricPenaltiesSummary> & {
  yearly_totals?: DashboardHistoricPenaltyYear[];
};

function emptyHistoricSummary(): DashboardHistoricPenaltiesSummary {
  return {
    total_amount: 0,
    total_violations: 0,
    most_cited_tag: null,
    most_cited_count: 0,
    yearly_totals: [],
    has_data: false,
  };
}

function historicHasData(block: Partial<DashboardHistoricPenaltiesSummary> | undefined): boolean {
  if (!block) return false;
  if (block.has_data === true) return true;
  return (
    (block.total_violations ?? 0) > 0 ||
    (block.total_amount ?? 0) > 0 ||
    (block.yearly_totals?.some((y) => y.amount > 0 || y.violation_count > 0) ?? false)
  );
}

/** True when any completed year has penalties or violations (ignores API has_data flag). */
export function historicPenaltiesHasYearData(
  block: Partial<DashboardHistoricPenaltiesSummary> | undefined,
): boolean {
  if (!block) return false;
  return (
    block.yearly_totals?.some((y) => y.amount > 0 || y.violation_count > 0) ?? false
  );
}

/** Canonical facility type slug for historic-penalties gating (matches Penalty cyberFacilityType). */
export function resolveFacilityTypeSlugForHistoricPenalties(
  facilityTypeSlug?: string | null,
  facilityType?: string | null,
): string {
  const slug = facilityTypeSlug?.trim();
  if (slug) return slug.toLowerCase().replace(/-/g, "_");
  let normalized = (facilityType ?? "")
    .toLowerCase()
    .trim()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");
  const paren = normalized.indexOf("_(");
  if (paren > 0) normalized = normalized.slice(0, paren);
  return normalized;
}

/**
 * Individual dashboard: show facility historic penalties when CMS NH PDC (always) or any year has data.
 */
export function shouldShowFacilityHistoricPenaltiesSection(
  facility: CmsNursingHomePdcFacilityLike | null | undefined,
  facilityTypeSlug: string,
  penalties: Partial<DashboardHistoricPenaltiesSummary> | undefined,
): boolean {
  if (isCmsNursingHomePdcFacility(facility) || isCmsNursingHomePdcSlug(facilityTypeSlug)) {
    return true;
  }
  return historicPenaltiesHasYearData(penalties);
}

function toHistoricSummary(
  raw: LegacyHistoricPenalties | undefined,
): DashboardHistoricPenaltiesSummary {
  if (!raw) return emptyHistoricSummary();
  const yearly = raw.yearly_totals ?? [];
  const summary: DashboardHistoricPenaltiesSummary = {
    total_amount: raw.total_amount ?? 0,
    total_violations: raw.total_violations ?? 0,
    most_cited_tag: raw.most_cited_tag ?? null,
    most_cited_count: raw.most_cited_count ?? 0,
    yearly_totals: yearly,
    has_data: raw.has_data ?? historicHasData(raw),
  };
  return summary;
}

/**
 * Maps API payloads (including Redis-cached legacy `historic_penalties`) to the current shape.
 */
export function normalizeFacilityDashboardResponse(
  raw: Record<string, unknown>,
): FacilityIndividualDashboardResponse {
  const legacy = raw.historic_penalties as LegacyHistoricPenalties | undefined;
  const industryRaw = raw.historic_penalties_industry as
    | DashboardHistoricPenaltiesIndustrySummary
    | undefined;
  const facilityRaw = raw.historic_penalties_facility as
    | DashboardHistoricPenaltiesFacilitySummary
    | undefined;

  let historic_penalties_industry: DashboardHistoricPenaltiesIndustrySummary;
  let historic_penalties_facility: DashboardHistoricPenaltiesFacilitySummary;

  if (industryRaw) {
    historic_penalties_industry = {
      ...toHistoricSummary(industryRaw),
      facility_type_label: industryRaw.facility_type_label ?? null,
      facilities_analyzed_count: industryRaw.facilities_analyzed_count ?? 0,
      industry_data_source: industryRaw.industry_data_source ?? null,
    };
  } else if (legacy) {
    const base = toHistoricSummary(legacy);
    historic_penalties_industry = {
      ...base,
      facility_type_label: null,
      facilities_analyzed_count: 0,
    };
  } else {
    historic_penalties_industry = {
      ...emptyHistoricSummary(),
      facility_type_label: null,
      facilities_analyzed_count: 0,
    };
  }

  if (facilityRaw) {
    historic_penalties_facility = toHistoricSummary(facilityRaw);
  } else {
    historic_penalties_facility = emptyHistoricSummary();
  }

  const tilesRaw = raw.tiles as Record<string, unknown> | undefined;
  const analysesRaw = tilesRaw?.analyses_run as Record<string, unknown> | undefined;
  const lastRunAt =
    (analysesRaw?.last_run_at as string | null | undefined) ??
    (analysesRaw?.lastRunAt as string | null | undefined) ??
    null;

  const base = raw as unknown as FacilityIndividualDashboardResponse;
  const regRaw = tilesRaw?.regulation_updates as Record<string, unknown> | undefined;
  const somTagsRaw = regRaw?.som_tags;
  const som_tags = Array.isArray(somTagsRaw)
    ? somTagsRaw
        .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
        .map((row) => ({
          tag: String(row.tag ?? ""),
          section_id:
            row.section_id != null && row.section_id !== ""
              ? Number(row.section_id)
              : null,
        }))
        .filter((row) => row.tag.length > 0)
    : [];
  const regulation_tags = Array.isArray(regRaw?.regulation_tags)
    ? (regRaw!.regulation_tags as string[])
  : som_tags.map((t) => t.tag);

  const calendarStripRaw = raw.calendar_strip as
    | { month_label?: string; year?: number; events?: DashboardCalendarEvent[] }
    | undefined;
  const profileRaw = raw.profile_completeness as
    | Partial<DashboardProfileCompleteness>
    | undefined;
  const penaltyTileRaw = tilesRaw?.penalty_exposure as
    | Partial<DashboardPenaltyExposureTile>
    | undefined;

  return {
    ...base,
    historic_penalties_industry,
    historic_penalties_facility,
    cms_nursing_home_pdc: (raw.cms_nursing_home_pdc as CmsNursingHomePdcMeta | undefined) ?? base.cms_nursing_home_pdc,
    action_required: Array.isArray(base.action_required) ? base.action_required : [],
    regulation_impact: Array.isArray(base.regulation_impact) ? base.regulation_impact : [],
    document_expiry: Array.isArray(base.document_expiry) ? base.document_expiry : [],
    calendar_strip: {
      month_label: calendarStripRaw?.month_label ?? "—",
      year: calendarStripRaw?.year ?? new Date().getFullYear(),
      events: Array.isArray(calendarStripRaw?.events) ? calendarStripRaw!.events : [],
    },
    profile_completeness: {
      completed_count: profileRaw?.completed_count ?? 0,
      total_count: profileRaw?.total_count ?? 0,
      completeness_percentage: profileRaw?.completeness_percentage ?? 0,
      missing_fields: Array.isArray(profileRaw?.missing_fields)
        ? profileRaw!.missing_fields
        : [],
      // AB#10180 / SSOT-59 — keep optional gaps so Completeness card/tooltip can list them
      missing_optional_fields: Array.isArray(profileRaw?.missing_optional_fields)
        ? profileRaw!.missing_optional_fields
        : [],
    },
    profile_schema_warmed: raw.profile_schema_warmed === true,
    tiles: base.tiles
      ? {
          ...base.tiles,
          penalty_exposure: {
            ...base.tiles.penalty_exposure,
            sparkline_points: Array.isArray(penaltyTileRaw?.sparkline_points)
              ? penaltyTileRaw!.sparkline_points
              : base.tiles.penalty_exposure?.sparkline_points ?? [],
          },
          analyses_run: {
            ...base.tiles.analyses_run,
            last_run_at: lastRunAt,
          },
          regulation_updates: {
            ...base.tiles.regulation_updates,
            som_tags,
            regulation_tags,
            last_som_updated_display:
              (regRaw?.last_som_updated_display as string | undefined) ?? "—",
            last_som_updated_at:
              (regRaw?.last_som_updated_at as string | null | undefined) ?? null,
          },
        }
      : base.tiles,
  };
}

export interface FacilityIndividualDashboardResponse {
  success: boolean;
  message: string;
  facility_id: number;
  country_code: string;
  cached: boolean;
  impact_assessment_version: string;
  tiles: {
    penalty_exposure: DashboardPenaltyExposureTile;
    regulation_updates: DashboardRegulationUpdatesTile;
    analyses_run: DashboardAnalysesRunTile;
  };
  calendar_strip: DashboardCalendarStrip;
  historic_penalties_industry: DashboardHistoricPenaltiesIndustrySummary;
  historic_penalties_facility: DashboardHistoricPenaltiesFacilitySummary;
  cms_nursing_home_pdc?: CmsNursingHomePdcMeta;
  action_required: DashboardActionRequiredItem[];
  regulation_impact: DashboardRegulationImpactItem[];
  document_expiry: DashboardDocumentExpiryItem[];
  profile_completeness: DashboardProfileCompleteness;
  profile_schema_warmed?: boolean;
}

export interface FacilityNetworkOverviewTopExposure {
  facility_id: number;
  name: string;
  type_label: string;
  location: string;
  meta_line: string;
  exposure: number;
  exposure_display: string;
  city?: string | null;
  state?: string | null;
  facility_type?: string | null;
}

export interface FacilityNetworkOverviewTrendYear {
  year: number;
  amount: number;
}

/** Parent Overview aggregate BFF — one call for all linked children. */
export interface FacilityNetworkOverviewResponse {
  success: boolean;
  message: string;
  parent_facility_id: number;
  country_code: string;
  total_penalty_exposure: number;
  total_penalty_display: string;
  facilities_with_gaps: number;
  facilities_clean: number;
  surveys_due_90d: number;
  gaps_by_type: Record<string, number>;
  top_exposure: FacilityNetworkOverviewTopExposure[];
  trend_years: FacilityNetworkOverviewTrendYear[];
  loaded_count: number;
  failed_count: number;
  linked_count: number;
}

export async function getFacilityDashboard(
  facilityId: number,
  userId: number,
  countryCode?: string,
): Promise<FacilityIndividualDashboardResponse> {
  const baseUrl = (API_BASE_URL || "").replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }
  const cc = countryCode || getSelectedCountryCode();
  const url = new URL(`${baseUrl}${DASHBOARD_PATH}/${facilityId}/dashboard`);
  url.searchParams.set("user_id", String(userId));
  url.searchParams.set("country_code", cc);

  const token = getValidAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const controller = new AbortController();
  const timeoutMs = DASHBOARD_FETCH_TIMEOUT_MS;
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      priority: "high",
      signal: controller.signal,
    } as RequestInit);
  } catch (e) {
    if (timedOut) {
      throw new Error(
        `Dashboard request timed out after ${Math.round(timeoutMs / 1000)}s — try again`,
      );
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
  check401(response);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Dashboard request failed (${response.status})`);
  }
  const raw = (await response.json()) as Record<string, unknown>;
  return normalizeFacilityDashboardResponse(raw);
}

export async function getFacilityNetworkOverview(
  parentFacilityId: number,
  userId: number,
  countryCode?: string,
): Promise<FacilityNetworkOverviewResponse> {
  const baseUrl = (API_BASE_URL || "").replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }
  const cc = countryCode || getSelectedCountryCode();
  const url = new URL(
    `${baseUrl}${DASHBOARD_PATH}/${parentFacilityId}/network-overview`,
  );
  url.searchParams.set("user_id", String(userId));
  url.searchParams.set("country_code", cc);

  const token = getValidAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const controller = new AbortController();
  const timeoutMs = DASHBOARD_FETCH_TIMEOUT_MS;
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      priority: "high",
      signal: controller.signal,
    } as RequestInit);
  } catch (e) {
    if (timedOut) {
      throw new Error(
        `Network overview request timed out after ${Math.round(timeoutMs / 1000)}s — try again`,
      );
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
  check401(response);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Network overview request failed (${response.status})`);
  }
  return (await response.json()) as FacilityNetworkOverviewResponse;
}
